
  import type { PropType } from 'vue';
  import { defineComponent } from 'vue';
  import { RouterLink } from 'vue-router';

  import PaginationRedesigned from '@/common/components/redesigned/PaginationRedesigned.vue';
  import TableSortableHeader from '@/common/components/redesigned/Table/TableSortableHeader.vue';
  import { TStorePaths } from '@/common/types/storePaths';
  import IconComponent from '@/common/ui/IconComponent.vue';
  import { isAbsoluteUrl } from '@/common/utils/url';

  import Loader from '@/components/Loader.vue';

  import type {
    TableEventIconsTypes,
    TPaginationConfig,
    TSortParams,
    TTableColumnConfig,
    TTableData,
    TTableEventConfigs,
    TTableEventIconsConfig,
    TTableEventIconsConfigs,
    TTableWithOuterFilterConfig,
  } from './types/tableTypes';
  import { TableEventTypes } from './types/tableTypes';
  import { PaginationFilter, SortFilter } from './utils/filters';

  import { TPageMetadata } from '@/types/meta';

  type TTableProps = {
    columns: TTableColumnConfig<TTableData<any>>[];
    params: TTableWithOuterFilterConfig['params'];
    pagination?: TPaginationConfig;
  };

  type TResponseData = {
    data: TPageMetadata & { items: TTableData[] };
  };

  const EVENT_ICONS_NAMES_MAP: Record<TableEventTypes, string | null> = {
    [TableEventTypes.EDIT]: 'edit',
    [TableEventTypes.DELETE]: 'delete-not-fill',
    [TableEventTypes.ROW_CLICK]: null,
  };

  export default defineComponent({
    name: 'TableComponent',
    emits: ['sortChange'],
    components: {
      PaginationRedesigned,
      TableSortableHeader,
      Loader,
      IconComponent,
      RouterLink,
    },
    data(): {
      isLoading: boolean;
      isLoadingMore: boolean;
      paginationFilter: PaginationFilter;
      sortFilter: SortFilter;
      abortController: AbortController | null;
      isFetchQueued: boolean;
    } {
      return {
        isLoading: false,
        isLoadingMore: false,
        paginationFilter: new PaginationFilter({ name: 'filter' }),
        sortFilter: new SortFilter({ name: 'sort' }),
        abortController: null,
        isFetchQueued: false,
      };
    },
    props: {
      columns: {
        type: Array as PropType<TTableProps['columns']>,
        required: true,
      },
      params: {
        type: Object as PropType<TTableProps['params']>,
      },
      emptyText: {
        type: String,
        default: 'Нет данных',
      },
      pagination: {
        type: Object as PropType<TTableProps['pagination']>,
        default: () => ({
          size: 10,
        }),
      },
      storePaths: {
        type: Object as PropType<TStorePaths>,
        required: true,
      },
      tableClass: {
        type: String,
      },
      isShowNumberColumn: {
        type: Boolean,
        default: false,
      },
      showPagination: {
        type: Boolean,
        default: true,
      },
      eventConfigs: {
        type: Array as PropType<TTableEventConfigs>,
      },
      /**
       * @description Триггер для обновления данных в таблице
       * @description При изменении значения, данные в таблице будут обновлены
       */
      updateTrigger: {
        type: Number,
        default: 0,
      },
      /**
       * @description Класс строки по данным строки (например, подсветка по флагу)
       */
      rowClass: {
        type: Function as PropType<(row: TTableData) => string | undefined>,
      },
      defaultSort: {
        type: Object as PropType<TSortParams | null>,
        default: null,
      },
    },
    created() {
      if (this.defaultSort) this.sortFilter.setValue(this.defaultSort);
      this.setPaginationFilter(1);
      this.fetchData();
    },
    computed: {
      sortValue(): TSortParams | null {
        return this.sortFilter.getValue() ?? null;
      },
      getPreparedData(): Array<TTableData> {
        return this.getItems.map((item) => {
          // TODO: подумать над реализацией с лучшей типизацией
          return {
            id: item.id,
            ...this.columns.reduce<Record<string, string | number | boolean | Record<string, unknown>>>(
              (acc, column) => {
                if (column.renderComponent) {
                  acc[column.field] = item[column.field];

                  return acc;
                }

                acc[column.field] = column.formatter ? column.formatter(item) : item[column.field];

                return acc;
              },
              {},
            ),
          };
        });
      },
      isShowPagination() {
        return this.showPagination && this.getMeta.pagesTotal > 1;
      },
      getItems(): Array<TTableData> {
        return this.$store.getters[this.storePaths.getter]?.items ?? [];
      },
      getMeta() {
        const getterDate = this.$store.getters[this.storePaths.getter];
        return {
          pageSize: getterDate?.pageSize ?? this.pagination?.size ?? 0,
          pageNumber: getterDate?.pageNumber ?? 1,
          pagesTotal: getterDate?.pagesTotal ?? 0,
          itemsTotal: getterDate?.itemsTotal ?? 0,
          itemCount: getterDate?.itemCount ?? 0,
          firstItemNumber: getterDate?.firstItemNumber ?? 1,
        };
      },
      getIsEmpty() {
        return this.getItems.length === 0 && !this.isLoading;
      },
      getEmptyCellColspan() {
        return this.columns.length + (this.eventConfigs?.length ?? 0) + (this.isShowNumberColumn ? 1 : 0);
      },
      getEventIconsConfigs(): TTableEventIconsConfigs {
        return (this.eventConfigs ?? []).filter(
          (config): config is TTableEventIconsConfig => !!EVENT_ICONS_NAMES_MAP[config.type],
        );
      },
      getEventIconsNamesMap() {
        return (this.getEventIconsConfigs ?? []).reduce(
          (acc, { type }) => {
            const icon = EVENT_ICONS_NAMES_MAP[type];

            if (icon) {
              acc[type] = icon;
            }

            return acc;
          },
          {} as Record<TableEventIconsTypes, string>,
        );
      },
      getRowClickHandle() {
        return this.eventConfigs?.find((config) => config.type === TableEventTypes.ROW_CLICK);
      },
      isRowClickable() {
        return !!this.getRowClickHandle;
      },
    },
    methods: {
      async searchData(params: TTableWithOuterFilterConfig['params']) {
        return await this.$store.dispatch(this.storePaths.action, {
          params,
          abortController: this.abortController,
        });
      },
      setData(data: TResponseData, isMore: boolean) {
        let result = data;

        if (isMore) {
          const oldValue = this.$store.getters[this.storePaths.getter];
          result = {
            ...result,
            data: {
              ...result.data,
              items: [...oldValue.items, ...data.data.items],
              firstItemNumber: oldValue.firstItemNumber,
            },
          };
        }

        this.$store.commit(this.storePaths.mutation, { ...result });
      },
      async fetchData(isMore = false) {
        if (isMore) {
          this.isLoadingMore = true;
        } else {
          this.isLoading = true;
        }

        if (this.abortController) {
          this.abortController.abort();
        }
        this.abortController = new AbortController();

        const allParams: TTableWithOuterFilterConfig['params'] = { ...this.params };

        [this.paginationFilter, this.sortFilter].forEach((filter) => {
          const entries = filter.toEntries();
          if (entries) {
            entries.forEach(([key, value]) => {
              allParams[key] = value;
            });
          }
        });

        try {
          const data: TResponseData = await this.searchData(allParams);
          this.setData(data, isMore);
        } catch (error) {
          console.error(error);
        } finally {
          this.isLoading = false;
          this.isLoadingMore = false;
        }
      },
      getThWidth(column: TTableColumnConfig<TTableData>) {
        const width = this.columns.find((c) => c.field === column.field)?.width;
        const cssWidth = width ? `${width}px` : 'auto';

        return {
          minWidth: cssWidth,
        };
      },
      onSortChange(sort: TSortParams | null) {
        this.sortFilter.setValue(sort);
        this.$emit('sortChange', sort);
        this.resetPagination();
      },
      // вызывается извне по ref
      // eslint-disable-next-line vue/no-unused-properties
      resetSort() {
        if (this.sortValue) this.onSortChange(null);
      },
      onPageChange(page: number) {
        this.setPaginationFilter(page);
        this.fetchData();
      },
      onPageMore() {
        this.setPaginationFilter(this.getMeta.pageNumber + 1);
        this.fetchData(true);
      },
      resetPagination() {
        this.paginationFilter.setValue({ size: this.pagination?.size ?? 0, number: 1 });
        this.queueFetch();
      },
      queueFetch() {
        if (this.isFetchQueued) return;
        this.isFetchQueued = true;
        this.$nextTick(() => {
          this.isFetchQueued = false;
          this.fetchData();
        });
      },
      setPaginationFilter(page: number) {
        this.paginationFilter.setValue({ size: this.pagination?.size ?? 0, number: page });
      },
      getRenderComponentProps(column: TTableColumnConfig<TTableData>, item: TTableData, index: number) {
        if (!column.renderComponent) return {};

        if (column.renderComponent.props) {
          return column.renderComponent.props(item, index);
        }

        return {
          ...(typeof item[column.field] === 'object' && item[column.field] !== null
            ? (item[column.field] as Record<string, unknown>)
            : {}),
        };
      },
      handleRowClick(item: object) {
        this.getRowClickHandle?.func(item, this.resetPagination);
      },
      getRowClass(index: number): string {
        return this.rowClass?.(this.getItems[index]) ?? '';
      },
      isAbsoluteUrl(link: string) {
        return isAbsoluteUrl(link);
      },
    },
    watch: {
      params: {
        handler() {
          this.resetPagination();
        },
        deep: true,
      },
      updateTrigger() {
        this.fetchData();
      },
    },
  });
